import { CaretDown, CaretUp, ChevronRight } from '@carbon/icons-react'
import { DDSRadio, DDSTooltip, DDSTypography } from 'den-design-system'
import {
  ExceptionStatusTypes,
  SeverityTypes
} from '../../../../api/exception/Exception.types'
import React, { useEffect, useRef, useState } from 'react'
import {
  bulkUpdateExceptionsForMultipleGroups,
  bulkUpdateExceptionsInGroup,
  getExceptionGroupFilters,
  updateExceptionGroup,
  viewAllGroupedExceptions
} from '../../../../api/exception/Exception.service'
import {
  formatMonth,
  getCommonFilters,
  getOrgTimeRange,
  timeZone
} from '../../../../utils/DateTimeUtils'
import {
  getAllQueryParamsAsObjectFromUrl,
  replaceUrl
} from '../../../../utils/UrlUtils'
import { isEmpty, isEqual, isNull, isUndefined } from 'lodash'
import { useNavigate, useSearchParams } from 'react-router-dom'

import ConfirmationPopup from '../../../../components/common/Exceptions/ConfirmationPopup'
import EmptyAvatar from '../../../../assets/images/EmptyAvatar.svg'
import Filter from '../../../../components/Filter/Filter'
import HorizontalStackedProgressBar from '../../../../components/common/DDS/StackedProgressBar/HorizontalStackedProgressBar'
import { Link } from '@mui/material'
import NoFilterImage from '../../../../assets/images/folder-not-found 1.svg'
import NoRecord from '../../../../components/common/NoContent/NoRecord'
import NoRecordImage from '../../../../assets/images/setting.svg'
import { ProjectType } from '../../../../enum/ProjectType.enum'
import ResponseTimeGraph from '../../../../components/AHCPage/AHCOverview/ResponseTimeGraph'
import Search from '../../../../components/New/Search/Search'
import SeverityDropDown from '../../../../components/common/Exceptions/SeverityDropdown'
import SideChip from '../../../../components/New/SideChip/SideChip'
import Spinner from '../../../../components/common/Spinner/Spinner'
import StringConstants from '../../../../constants/StringConstants'
import Table from '../../../../components/common/DDS/Table/Table'
import { ToastMessageConstants } from '../../../../constants/ToastMessageConstants'
import ToastNotification from '../../../../components/common/DDS/Toast/Toast'
import UserAvatar from '../../../../components/common/UserAvatar/UserAvatar'
import { convertToFormat } from '../../../../utils/UCUtils'
import { generateRandomId } from '../../../../utils/CommonUtils'
import { observer } from 'mobx-react-lite'
import pallete from '../../../../global/newPallete'
import routeConstants from '../../../../constants/RouteConstants'
import { severityColorMap } from '../../../../constants/ExceptionsPageConstants'
import { timeFormats } from '../../../../enum/TIME'
import { useStoreContext } from '../../../../store/StoreContext'

type SortOrder = 'ASC' | 'DESC' | 'CLEAR'
type SortState = null | SortOrder | 'CLEAR'

const StatusCodeGraphMapping = {
  Unresolved: '#B9B9B9',
  Resolved: '#56B320'
}

const SeverityColorMapping = {
  Critical: pallete.colors.warningRed2,
  Major: pallete.colors.warningOrange,
  Moderate: pallete.colors.warningBlue,
  Minor: pallete.colors.textDark6
}

const GroupedExceptions: React.FC = () => {
  const [tableData, setTableData] = useState<any>()
  const [modalOpen, setModalOpen] = React.useState<boolean>(false)
  const [currentGroupId, setCurrentGroupId] = useState<any>('')
  const [preference, setPreference] = React.useState<string>('')
  const [changeToPreference, setChangeToPreference] = useState<any>('')
  const [selectedRows, setSelectedRows] = useState<any>([])
  const [totalCount, setTotalCount] = useState(0)
  const [dateUserFilter, setDateUserFilter] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [filterLoading, setFilterLoading] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [searchParams, setSearchParams] = useSearchParams()
  const [applyToExisting, setApplyToExisting] = useState<boolean | null>(null)

  const navigate = useNavigate()
  const store = useStoreContext()

  const [searchRecordLength, setSearchRecordLength] = useState<number>(0)
  const tableRef = useRef<HTMLDivElement>(null)
  const previousPayloadRef = useRef<any>(null)

  const [activeSortOrder, setActiveSortOrder] = useState<{
    field: string | null
    order: SortOrder | null
  }>(() => {
    const searchParams = new URLSearchParams(window.location.search)

    let sortField: string | null = null
    let sortOrder: SortOrder | null = null

    searchParams.forEach((value, key) => {
      if (key.startsWith('sort-') && (value === 'ASC' || value === 'DESC')) {
        sortField = key
        sortOrder = value as SortOrder
      }
    })

    return { field: sortField, order: sortOrder }
  })

  useEffect(() => {
    if (activeSortOrder.field && activeSortOrder.order) {
      sortData(activeSortOrder.field, activeSortOrder.order)
    }
  }, [])

  const [tableHeight, setTableHeight] = useState<number | null>(null)

  const projectType = store.scopeStore.getSelectedProject().type
  const isFullStackProject = projectType === ProjectType.FULL_STACK_ONLY
  const isSalesforceProject = projectType === ProjectType.SALESFORCE_ONLY
  const [searchTerm, setSearchTerm] = useState('') // Holds the search term entered by the user
  const [showDropdown, setShowDropdown] = useState(false)
  const rowDisabled = useRef(false)
  const [filterOptions, setFilterOptions] = useState<any>([])
  const [dateFilter, setDateFilter] = useState<any>({})
  const organizationTimeZone =
    store.userStore.getOrganizationPreference()?.timeZone

  const updatePreferences = () => {
    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)
    const isSameSeverity =
      tableData.filter(
        (data: any) => data.exceptionGroupId === currentGroupId
      )[0].preferredSeverity === preference
    if (isSameSeverity) {
      ToastNotification({
        type: 'error',
        message: 'Update failed. The selected severity is already applied.'
      })
      setModalOpen(false)
      return
    }
    if (selectedRows.length === 0) {
      if (!isSameSeverity) {
        setModalOpen(true)

        setLoading(true)
        updateExceptionGroup({
          ids: [currentGroupId as string],
          preferredSeverity: preference as SeverityTypes
        })
          .then(() => {
            ToastNotification({
              type: 'success',
              message: isFullStackProject
                ? ToastMessageConstants.EXCEPTIONS.UPDATE_GROUP_DEFAULT_SEVERITY
                    .SUCCESS
                : ToastMessageConstants.ERROR.UPDATE_GROUP_DEFAULT_SEVERITY
                    .SUCCESS
            })
            setModalOpen(false)
            setCurrentGroupId('')
            setPreference('')
            setApplyToExisting(null)
            if (applyToExisting) {
              const orgTimeZone =
                store.userStore.getOrganizationPreference()?.timeZone

              const payload: any = {
                exceptionGroupId: currentGroupId as string,
                'created-from': allQueryParams['created-from'],
                'created-to': allQueryParams['created-to'],
                severity: preference as SeverityTypes,
                'time-zone': orgTimeZone
              }
              bulkUpdateExceptionsInGroup(payload).then((response: any) => {
                if (response.data.hasMore) {
                  ToastNotification({
                    type: 'info',
                    message: isFullStackProject
                      ? ToastMessageConstants.EXCEPTIONS
                          .BULK_UPDATE_EXCEPTIONS_IN_GROUP.REMAINING_BATCHES
                      : ToastMessageConstants.ERROR.BULK_UPDATE_ERRORS_IN_GROUP
                          .REMAINING_BATCHES
                  })
                } else {
                  ToastNotification({
                    type: 'success',
                    message: isFullStackProject
                      ? ToastMessageConstants.EXCEPTIONS
                          .BULK_UPDATE_EXCEPTIONS_IN_GROUP.SUCCESS
                      : ToastMessageConstants.ERROR.BULK_UPDATE_ERRORS_IN_GROUP
                          .SUCCESS
                  })
                }
                fetchGroupedExceptions({
                  ...allQueryParams,
                  size: StringConstants.DEFAULT_TABLE_SIZE
                })
              })
            } else {
              fetchGroupedExceptions({
                ...allQueryParams,
                size: StringConstants.DEFAULT_TABLE_SIZE
              })
            }
          })

          .catch((err) => {
            ToastNotification({
              type: 'error',
              message: isFullStackProject
                ? ToastMessageConstants.EXCEPTIONS.UPDATE_GROUP_DEFAULT_SEVERITY
                    .ERROR
                : ToastMessageConstants.ERROR.UPDATE_GROUP_DEFAULT_SEVERITY
                    .ERROR
            })
            setLoading(false)

            store.filterStore.setFilterDisabled(false)
          })
      }
    } else {
      const exceptionGroupIds: string[] = []
      selectedRows.forEach((exception: any) =>
        exceptionGroupIds.push(exception.exceptionGroupId)
      )
      setLoading(true)
      updateExceptionGroup({
        ids: exceptionGroupIds,
        preferredSeverity: preference as SeverityTypes
      })
        .then(() => {
          ToastNotification({
            type: 'success',
            message: isFullStackProject
              ? ToastMessageConstants.EXCEPTIONS.UPDATE_AN_EXCEPTION.SUCCESS
              : ToastMessageConstants.ERROR.UPDATE_AN_ERROR.SUCCESS
          })
          setModalOpen(false)
          setCurrentGroupId('')
          setPreference('')
          setSelectedRows([])
          setApplyToExisting(null)
          if (applyToExisting) {
            const orgTimeZone =
              store.userStore.getOrganizationPreference()?.timeZone

            const payload: any = {
              exceptionGroupIds: exceptionGroupIds,
              'created-from': allQueryParams['created-from'],
              'created-to': allQueryParams['created-to'],
              severity: preference as SeverityTypes,
              'time-zone': orgTimeZone
            }
            bulkUpdateExceptionsForMultipleGroups(payload).then(() => {
              ToastNotification({
                type: 'info',
                message: isFullStackProject
                  ? ToastMessageConstants.EXCEPTIONS
                      .BULK_UPDATE_EXCEPTIONS_IN_GROUP.REMAINING_BATCHES
                  : ToastMessageConstants.ERROR.BULK_UPDATE_ERRORS_IN_GROUP
                      .REMAINING_BATCHES
              })
            })
          }
          fetchGroupedExceptions({
            ...allQueryParams,
            size: StringConstants.DEFAULT_TABLE_SIZE
          })
        })
        .catch((err: any) => {
          ToastNotification({
            type: 'error',
            message: isFullStackProject
              ? ToastMessageConstants.EXCEPTIONS.UPDATE_AN_EXCEPTION.ERROR
              : ToastMessageConstants.ERROR.UPDATE_AN_ERROR.ERROR
          })
        })
    }
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setApplyToExisting(null)
  }

  const routeException = (type: string, exceptionGroupId: string) => {
    if (!rowDisabled.current) {
      store.exceptionStore.setType(type)
      // Get the organization time zone or default to UTC
      const orgTimeZone = store.userStore.getOrganizationPreference()?.timeZone

      const { startOfMonth, endOfCurrentDayTimestamp } =
        getOrgTimeRange(orgTimeZone)

      const searchParams = new URLSearchParams(window.location.search)
      const currentFilters: Record<string, string> = {}

      ;(currentFilters['created-from'] =
        searchParams.get('created-from') || String(startOfMonth)),
        (currentFilters['created-to'] =
          searchParams.get('created-to') || String(endOfCurrentDayTimestamp)),
        (currentFilters['time-zone'] =
          searchParams.get('time-zone') || orgTimeZone)

      if (searchParams.get('assigned-to')) {
        currentFilters['assigned-to'] = searchParams.get('assigned-to') || ''
      }

      const route = replaceUrl(
        routeConstants.ERROR_MONITORING,
        store.scopeStore.getScope()['organization-id'] as string,
        store.scopeStore.getScope()['project-id'] as string,
        store.scopeStore.getScope()['environment-id'] as string
      ).replace(':id', exceptionGroupId)

      const newSearchParams = new URLSearchParams({
        page: '1',
        tab: '1',
        ...currentFilters
      })

      navigate(`${route}?${newSearchParams.toString()}`)
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 50)
    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)

    const timeZone = store.userStore.getOrganizationPreference()?.timeZone

    const { startOfMonth, endOfCurrentDayTimestamp } = getOrgTimeRange(timeZone)

    if (value === '' && allQueryParams['search-string']) {
      setSearchTerm('')
      setShowDropdown(false)
      setLoading(true)
      store.uiStore.setGlobalLoader(true)
      setCurrentPage(currentPage)

      const filteredQueryParams = {
        ...allQueryParams,
        size: StringConstants.DEFAULT_TABLE_SIZE,
        page: currentPage,
        'created-from': allQueryParams['created-from'] || startOfMonth,
        'created-to': allQueryParams['created-to'] || endOfCurrentDayTimestamp,
        'time-zone': timeZone
      }
      delete filteredQueryParams['search-string']
      delete filteredQueryParams['search-type']

      setSearchParams({ ...filteredQueryParams, searchTerm: '' })
      viewAllGroupedExceptions(filteredQueryParams)
        .then((response: any) => {
          setTableData(response.data)
          setTotalCount(response.totalCount)
          setSearchRecordLength(response.totalCount)

          const filters = getCommonFilters(
            response,
            startOfMonth,
            endOfCurrentDayTimestamp,
            timeZone
          )

          setDateUserFilter(filters)
          store.filterStore.setFilters(response.filters)
        })
        .catch((e) => {
          ToastNotification({
            type: 'error',
            message: isFullStackProject
              ? ToastMessageConstants.EXCEPTIONS.GROUP_SEARCH.ERROR
              : ToastMessageConstants.ERROR.GET_ALL_ERROR.ERROR
          })
        })
        .finally(() => {
          store.uiStore.setGlobalLoader(false)
          setLoading(false)
        })
      return
    }

    const isValidInput = /^[\w\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/.test(
      value
    )
    if (!isValidInput) {
      return
    }
    setSearchTerm(value)
    if (value.trim()) {
      setShowDropdown(true)
    } else {
      setShowDropdown(false)
    }
  }
  const handleOptionSelect = (searchString: string, searchType: string) => {
    setLoading(true)
    store.uiStore.setGlobalLoader(true)
    setCurrentPage(1)
    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)
    const params: any = {
      ...allQueryParams,
      'search-string': searchString,
      'search-type': searchType,
      page: 1,
      size: StringConstants.DEFAULT_TABLE_SIZE
    }

    setSearchParams({ ...params, searchTerm: searchTerm })

    viewAllGroupedExceptions(params)
      .then((response: any) => {
        setTableData(response.data)
        setCurrentPage(response.page)
        setTotalCount(response.totalCount)
        setSearchRecordLength(response.totalCount)

        store.exceptionsStoreV2.setTotalCount(response.totalCount)
      })
      .catch((e) => {
        ToastNotification({
          type: 'error',
          message: isFullStackProject
            ? ToastMessageConstants.EXCEPTIONS.GROUP_SEARCH.ERROR
            : ToastMessageConstants.ERROR.GET_ALL_ERROR.ERROR
        })
      })
      .finally(() => {
        store.uiStore.setGlobalLoader(false)
        setLoading(false)
      })

    setShowDropdown(false)
  }
  const handleSearchEnter = (searchValue: string) => {
    if (!searchValue || searchValue.trim() === '') {
      return
    }
    setLoading(true)
    store.uiStore.setGlobalLoader(true)
    setCurrentPage(1)
    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)
    const params = {
      ...allQueryParams,
      'search-string': searchValue || '',
      'search-type': 'any',
      page: 1,
      size: StringConstants.DEFAULT_TABLE_SIZE
    }
    setSearchParams({ ...params, searchTerm: searchTerm })
    viewAllGroupedExceptions(params)
      .then((response: any) => {
        setTableData(response.data)
        setCurrentPage(response.page)
        setTotalCount(response.totalCount)
        setSearchRecordLength(response.totalCount)

        store.exceptionsStoreV2.setTotalCount(response.totalCount)
      })
      .catch((e) => {
        ToastNotification({
          type: 'error',
          message: isFullStackProject
            ? ToastMessageConstants.EXCEPTIONS.GROUP_SEARCH.ERROR
            : ToastMessageConstants.ERROR.GET_ALL_ERROR.ERROR
        })
      })
      .finally(() => {
        store.uiStore.setGlobalLoader(false)
        setLoading(false)
      })
    setShowDropdown(false)
  }

  useEffect(() => {
    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)
    const initialSearchTerm = allQueryParams['search-string'] || ''
    const searchType = allQueryParams['search-type'] || 'any'
    const searchString = allQueryParams['search-string'] || ''

    if (searchString) {
      setSearchTerm(searchString)
    }

    if (initialSearchTerm) {
      if (searchType === 'any') {
        handleSearchEnter(initialSearchTerm)
      } else {
        handleOptionSelect(initialSearchTerm, searchType)
      }
    }
  }, [])

  useEffect(() => {
    if (tableRef.current) {
      setTableHeight(tableRef.current.clientHeight)
    }
  }, [tableData, loading])
  useEffect(() => {
    // Current project and environment IDs from the store
    const currentProjectId = store.scopeStore.getScope()['project-id']
    const currentEnvId = store.scopeStore.getScope()['environment-id']

    // Extract project and environment IDs from the URL
    const urlParts = location.pathname.split('/')
    const urlProjectId = urlParts[urlParts.indexOf('projects') + 1]
    const urlEnvId = urlParts[urlParts.indexOf('environments') + 1]

    // Check if the project or environment has changed
    if (currentProjectId !== urlProjectId || currentEnvId !== urlEnvId) {
      setSearchTerm('')
    }
  }, [location.pathname])

  const GroupedExceptionColumns = [
    {
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] items-center'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-start flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              {isFullStackProject ? 'Exception Type' : 'Error Type'}
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-center -space-y-[6px]'
            onClick={() => handleSortCycle('sort-exception-group-type')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  activeSortOrder.field === 'sort-exception-group-type' &&
                  activeSortOrder.order === 'ASC'
                    ? pallete.colors.FillButton
                    : activeSortOrder.field === 'sort-exception-group-type' &&
                        activeSortOrder.order === 'DESC'
                      ? pallete.colors.outButtonDis
                      : pallete.colors.textDark6
              }}
              className='cursor-pointer'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  activeSortOrder.field === 'sort-exception-group-type' &&
                  activeSortOrder.order === 'ASC'
                    ? pallete.colors.outButtonDis
                    : activeSortOrder.field === 'sort-exception-group-type' &&
                        activeSortOrder.order === 'DESC'
                      ? pallete.colors.FillButton
                      : pallete.colors.textDark6
              }}
              className='cursor-pointer'
            />
          </div>
        </div>
      ),

      dataLabel: 'exceptionGroupType',
      render: (text: any) => (
        <DDSTooltip
          id={generateRandomId()}
          label={text}
          shape='roundedRectangle'
          position='top'
          theme='light'
          className={`border override-tooltip-arrow max-w-[90%] break-all ${
            typeof text === 'string' && text.length > 10 ? text : '!hidden'
          }`}
          style={{
            borderColor: pallete.colors.stroke2,
            zIndex: 99999
          }}
        >
          <div className='flex justify-start max-w-[85px]'>
            <DDSTypography.Paragraph
              variant='medium'
              size='para'
              className='truncate cursor-default w-[85px]'
              color={pallete.colors.textDark4}
            >
              {text}
            </DDSTypography.Paragraph>
          </div>
        </DDSTooltip>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: { width: '120px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] items-center'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-center flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Last Reported
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-center -space-y-[6px]'
            onClick={() => handleSortCycle('sort-latest-reported-time')}
          >
            <CaretUp
              size={12}
              className='cursor-pointer'
              style={{
                color:
                  activeSortOrder.field === 'sort-latest-reported-time' &&
                  activeSortOrder.order === 'ASC'
                    ? pallete.colors.FillButton
                    : activeSortOrder.field === 'sort-latest-reported-time' &&
                        activeSortOrder.order === 'DESC'
                      ? pallete.colors.outButtonDis
                      : pallete.colors.textDark6
              }}
            />
            <CaretDown
              size={12}
              className='cursor-pointer'
              style={{
                color:
                  activeSortOrder.field === 'sort-latest-reported-time' &&
                  activeSortOrder.order === 'ASC'
                    ? pallete.colors.outButtonDis
                    : activeSortOrder.field === 'sort-latest-reported-time' &&
                        activeSortOrder.order === 'DESC'
                      ? pallete.colors.FillButton
                      : pallete.colors.textDark6
              }}
            />
          </div>
        </div>
      ),
      dataLabel: 'latestReportedTime',
      render: (text: any) => (
        <DDSTooltip
          id={generateRandomId()}
          label={formatMonth(
            timeZone(
              store.userStore.getOrganizationPreference().timeZone,
              text,
              timeFormats.fullMonthDateYearTime
            )
          )}
          shape='roundedRectangle'
          position='top'
          theme='light'
          className={`border override-tooltip-arrow ${
            typeof text === 'string' && text.length > 20 ? text : '!hidden'
          }`}
          style={{
            borderColor: pallete.colors.stroke2,
            zIndex: 99999,
            maxWidth: '90%'
          }}
        >
          <div className='flex justify-center items-center text-center  max-w-[120px]'>
            <DDSTypography.Paragraph
              variant='medium'
              size='para'
              className='text-center truncate cursor-default w-[85px]'
              color={pallete.colors.textDark4}
            >
              {timeZone(
                store.userStore.getOrganizationPreference().timeZone,
                text,
                timeFormats.fullMonthDateYearTime
              )}
            </DDSTypography.Paragraph>
          </div>
        </DDSTooltip>
      )
    },
    {
      heading: (
        <div className='flex justify-center whitespace-nowrap space-x-[8px] items-center'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-center flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Default Severity
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-center -space-y-[6px]'
            onClick={() => handleSortCycle('sort-preferred-severity')}
          >
            <CaretUp
              size={12}
              className='cursor-pointer'
              style={{
                color:
                  activeSortOrder.field === 'sort-preferred-severity' &&
                  activeSortOrder.order === 'ASC'
                    ? pallete.colors.FillButton
                    : activeSortOrder.field === 'sort-preferred-severity' &&
                        activeSortOrder.order === 'DESC'
                      ? pallete.colors.outButtonDis
                      : pallete.colors.textDark6
              }}
            />
            <CaretDown
              size={12}
              className='cursor-pointer'
              style={{
                color:
                  activeSortOrder.field === 'sort-preferred-severity' &&
                  activeSortOrder.order === 'ASC'
                    ? pallete.colors.outButtonDis
                    : activeSortOrder.field === 'sort-preferred-severity' &&
                        activeSortOrder.order === 'DESC'
                      ? pallete.colors.FillButton
                      : pallete.colors.textDark6
              }}
            />
          </div>
        </div>
      ),
      dataLabel: 'preferredSeverity',
      render: (text: any, fullItem: any, index?: number) => {
        // If there are selected rows, check if current row is NOT in the selection
        const isDropdownDisabled =
          selectedRows.length > 0 &&
          !selectedRows.some(
            (selectedRow: any) =>
              selectedRow.exceptionGroupId === fullItem.exceptionGroupId
          )

        const setPreferences = (severity: SeverityTypes) => {
          setCurrentGroupId(fullItem.exceptionGroupId)
          setPreference(severity)
          if (
            selectedRows.length === 0 &&
            fullItem.preferredSeverity !== severity
          ) {
            setModalOpen(true)
          } else if (selectedRows.length > 0) {
            setModalOpen(true)
          }
        }

        return (
          <div className='flex justify-center'>
            <SeverityDropDown
              label={text}
              onSelect={setPreferences}
              insideTable
              disabled={isDropdownDisabled}
              disableNoSubscription={
                store.userStore.getNoActiveSubscription() ||
                loading ||
                store.uiStore.getGlobalLoader()
              }
            />
          </div>
        )
      }
    },
    {
      columnHeaderStyle: { width: '116px' },

      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] items-center'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-center flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              {isFullStackProject ? 'Exception Count' : 'Error Count'}
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-center -space-y-[6px]'
            onClick={() => handleSortCycle('sort-exception-count')}
          >
            <CaretUp
              size={12}
              className='cursor-pointer'
              style={{
                color:
                  activeSortOrder.field === 'sort-exception-count' &&
                  activeSortOrder.order === 'ASC'
                    ? pallete.colors.FillButton
                    : activeSortOrder.field === 'sort-exception-count' &&
                        activeSortOrder.order === 'DESC'
                      ? pallete.colors.outButtonDis
                      : pallete.colors.textDark6
              }}
            />
            <CaretDown
              size={12}
              className='cursor-pointer'
              style={{
                color:
                  activeSortOrder.field === 'sort-exception-count' &&
                  activeSortOrder.order === 'ASC'
                    ? pallete.colors.outButtonDis
                    : activeSortOrder.field === 'sort-exception-count' &&
                        activeSortOrder.order === 'DESC'
                      ? pallete.colors.FillButton
                      : pallete.colors.textDark6
              }}
            />
          </div>
        </div>
      ),
      dataLabel: 'exceptionCount',
      columnDataStyle: { paddingX: '16px', width: '116px' },
      render: (text: any) => (
        <div className='flex justify-center'>
          <DDSTypography.Paragraph
            variant='semiBold'
            size='para'
            className='text-center w-[90px]'
            color={pallete.colors.textDark4}
          >
            {text}
          </DDSTypography.Paragraph>
        </div>
      )
    },
    // {
    //   columnHeaderStyle: { width: '116px' },
    //   heading: (
    //     <div className='flex items-center justify-center space-x-2 whitespace-nowrap'>
    //       <div>
    //         <DDSTypography.Paragraph
    //           variant='semiBold'
    //           size='caption'
    //           className='text-center'
    //           color={pallete.colors.textDark6}
    //         >
    //           Tags
    //         </DDSTypography.Paragraph>
    //       </div>
    //     </div>
    //   ),
    //   dataLabel: 'tags',

    //   render: (text: any, fullItem: any) => {
    //     const tags = fullItem.tags || [] // Safely handle cases where tags might be undefined or null
    //     const tagsLength = tags.length

    //     return (
    //       <div className='flex items-center justify-center'>
    //         <div>
    //           {tagsLength > 0 ? (
    //             <DDSTooltip
    //               className='border override-tooltip-arrow'
    //               id={generateRandomId()}
    //               label={
    //                 <div className='p-[12px] z-[99999] w-full'>
    //                   <div className='text-start mb-[10px]'>
    //                     <DDSTypography.Paragraph
    //                       variant='medium'
    //                       size='para'
    //                       color={pallete.colors.textDark4}
    //                       className='text-start cursor-default'
    //                     >
    //                       Tags Added to this Exception
    //                     </DDSTypography.Paragraph>
    //                   </div>
    //                   <div className='grid grid-cols-2 gap-y-[8px] w-[200px] gap-x-[6px]'>
    //                     {tags.map((tag: string, index: number) => (
    //                       <div
    //                         key={index}
    //                         className=' px-[8px] py-[8px] rounded-full'
    //                         style={{ backgroundColor: pallete.colors.surface4 }}
    //                       >
    //                         <DDSTypography.Paragraph
    //                           variant='medium'
    //                           size='para'
    //                           color={pallete.colors.textDark4}
    //                           className='text-center max-w-[200px] truncate break-words cursor-default'
    //                         >
    //                           {tag}
    //                         </DDSTypography.Paragraph>
    //                       </div>
    //                     ))}
    //                   </div>
    //                 </div>
    //               }
    //               shape='roundedRectangle'
    //               theme='light'
    //               style={{
    //                 borderColor: pallete.colors.stroke2,
    //                 zIndex: 999999
    //               }}
    //             >
    //               <div className='flex items-center gap-[4px]'>
    //                 <div>
    //                   <DDSTypography.Paragraph
    //                     variant='semiBold'
    //                     size='para'
    //                     color={pallete.colors.textDark4}
    //                   >
    //                     {tagsLength > 0 ? tagsLength : '-'}
    //                   </DDSTypography.Paragraph>
    //                 </div>
    //                 {tagsLength > 0 && (
    //                   <div>
    //                     <img src={Tags} alt='Tags Icon' />
    //                   </div>
    //                 )}
    //               </div>
    //             </DDSTooltip>
    //           ) : (
    //             <div className='flex items-center gap-[4px]'>
    //               <div>
    //                 <DDSTypography.Paragraph
    //                   variant='semiBold'
    //                   size='para'
    //                   color={pallete.colors.textDark4}
    //                 >
    //                   {tagsLength > 0 ? tagsLength : '-'}
    //                 </DDSTypography.Paragraph>
    //               </div>
    //               {tagsLength > 0 && (
    //                 <div>
    //                   <img src={Tags} alt='Tags Icon' />
    //                 </div>
    //               )}
    //             </div>
    //           )}
    //         </div>
    //       </div>
    //     )
    //   }
    // },
    {
      heading: (
        <span className='flex flex-col justify-center'>
          <DDSTypography.Paragraph
            variant='semiBold'
            size='caption'
            className='text-center flex justify-center'
            color={pallete.colors.textDark6}
          >
            Reported Time Graph
          </DDSTypography.Paragraph>
          <DDSTypography.Paragraph
            variant='semiBold'
            size='caption'
            className='text-center whitespace-nowrap flex justify-center'
            color={pallete.colors.textDark6}
          >
            (Last 7 Days)
          </DDSTypography.Paragraph>
        </span>
      ),
      dataLabel: 'reportedTimeGraph',
      render: (text: any, fullItem: any) => {
        const getResponseTimeGraphData = () => {
          if (
            !isNull(fullItem.responsesByInterval) &&
            !isUndefined(fullItem.responsesByInterval) &&
            Object.entries(fullItem.responsesByInterval).length > 1
          ) {
            const data = Object.entries(fullItem.responsesByInterval).map(
              ([key, value]: any, index: any) => {
                return {
                  x: (index as number) + 1,
                  y: fullItem.responsesByInterval[key] as number
                }
              }
            )

            return data
          } else {
            return []
          }
        }

        return (
          <div className='flex justify-center'>
            {isEmpty(getResponseTimeGraphData()) ? (
              '-'
            ) : (
              <ResponseTimeGraph data={getResponseTimeGraphData().reverse()} />
            )}
          </div>
        )
      }
    },
    {
      heading: (
        <DDSTypography.Paragraph
          variant='semiBold'
          size='caption'
          className='text-center whitespace-nowrap flex justify-center'
          color={pallete.colors.textDark6}
        >
          Assigned To
        </DDSTypography.Paragraph>
      ),
      dataLabel: 'assignedTo',
      render: (text: any, fullItem: any) => {
        const imageGroup = fullItem.assignedTo.map(
          (item: any, index: number) => {
            return {
              name: item.user.firstName + ' ' + item.user.lastName,
              avatar: item.user.avatar,
              firstName: item.user.firstName,
              lastName: item.user.lastName
            }
          }
        )

        return (
          <div className='flex flex-row justify-center w-full'>
            {fullItem.assignedTo.length === 1 ? (
              <UserAvatar
                avatarType='Single'
                imageUrl={fullItem.assignedTo[0].user.avatar}
                name={
                  fullItem.assignedTo[0].user.firstName +
                  ' ' +
                  fullItem.assignedTo[0].user.lastName
                }
                firstName={fullItem.assignedTo[0].user.firstName}
                lastName={fullItem.assignedTo[0].user.lastName}
                //imageObjectGroup={imageGroup}
              />
            ) : fullItem.assignedTo.length > 1 ? (
              <UserAvatar avatarType='Multiple' imageObjectGroup={imageGroup} />
            ) : (
              <img
                src={EmptyAvatar}
                className='rounded-full h-[24px] w-[24px]'
              />
            )}
          </div>
        )
      }
    },
    {
      heading: (
        <DDSTypography.Paragraph
          variant='semiBold'
          size='caption'
          className='text-center flex justify-center'
          color={pallete.colors.textDark6}
        >
          Severity Code Graph
        </DDSTypography.Paragraph>
      ),
      dataLabel: 'severity',
      columnHeaderStyle: { width: '127px' },
      columnDataStyle: { width: '127px' },
      render: (text: any, fullItem: any) => (
        <div className='flex justify-center w-[95px]'>
          <HorizontalStackedProgressBar
            totalCount={fullItem.exceptionCount}
            data={fullItem.severityCounts}
            color={SeverityColorMapping}
          />
        </div>
      )
    },
    {
      heading: (
        <DDSTypography.Paragraph
          variant='semiBold'
          size='caption'
          className=' text-center flex justify-center'
          color={pallete.colors.textDark6}
        >
          Status Code Graph
        </DDSTypography.Paragraph>
      ),
      columnDataStyle: { width: '110px' },
      columnHeaderStyle: { width: '110px' },
      dataLabel: 'exceptionStatusCounts',
      render: (item: any, fullItem: any) => (
        <div className='flex justify-center w-[72px]'>
          <HorizontalStackedProgressBar
            totalCount={fullItem.exceptionCount}
            data={item}
            color={StatusCodeGraphMapping}
          />
        </div>
      )
    },
    {
      columnHeaderStyle: { width: '20px' },
      columnDataStyle: { width: '20px' },
      heading: '',
      dataLabel: '',
      render: (text: any, fullItem: any) => {
        const isDropdownDisabled =
          selectedRows.length > 0 &&
          !selectedRows.some(
            (selectedRow: any) =>
              selectedRow.exceptionGroupId === fullItem.exceptionGroupId
          )
        return (
          <div>
            <ChevronRight
              size={16}
              color={pallete.colors.primary2}
              className={`${isDropdownDisabled} ? 'cursor-none' : 'cursor-pointer'`}
              onClick={() => {
                if (!isDropdownDisabled) {
                  routeException(
                    fullItem['exceptionGroupType'],
                    fullItem['exceptionGroupId']
                  )
                }
              }}
            />
          </div>
        )
      }
    }
  ]

  const fetchGroupedExceptions = (queryParams: any) => {
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)
    const searchParams = new URLSearchParams(location.search)
    const timeZone = store.userStore.getOrganizationPreference()?.timeZone

    const { startOfMonth, endOfCurrentDayTimestamp } = getOrgTimeRange(timeZone)

    const urlSortField = searchParams.get('sortField')
    const urlSortOrder = searchParams.get('sortOrder')

    const params: any = {
      ...allQueryParams,
      ...queryParams,
      'created-from': allQueryParams['created-from'] || startOfMonth,
      'created-to': allQueryParams['created-to'] || endOfCurrentDayTimestamp,
      'time-zone': allQueryParams['time-zone'] || timeZone,
      size: StringConstants.DEFAULT_TABLE_SIZE
    }

    if (urlSortField && urlSortOrder && urlSortOrder !== 'CLEAR') {
      params[urlSortField] = urlSortOrder
    }
    viewAllGroupedExceptions(params)
      .then((response: any) => {
        setTableData(response.data)
        setCurrentPage(response.page)
        store.exceptionsStoreV2.setGroupedExceptions(response.data)
        store.exceptionsStoreV2.setTotalCount(response.totalCount)
        setTotalCount(response.totalCount)
        store.filterStore.setFilters(response.filters)
        store.uiStore.setGlobalLoader(false)
        store.exceptionStore.setIsRefreshed(false)

        if (response.totalCount >= 1) {
          const progressData = store.thingsToDoStore.getProgressData()
          if (!progressData.exception) {
            const updatedProgressData = { ...progressData, exception: true }
            const newProgressCount = Object.entries(updatedProgressData).reduce(
              (count, [key, value]) =>
                typeof value === 'boolean' && value ? count + 1 : count,
              0
            )
            updatedProgressData.progress = newProgressCount
            store.thingsToDoStore.setProgressData(updatedProgressData)
          }
        }
        const filters = getCommonFilters(
          response,
          startOfMonth,
          endOfCurrentDayTimestamp,
          timeZone
        )

        store.filterStore.setFilters(response.filters)
        setDateFilter(filters)

        if (urlSortField && urlSortOrder) {
          setActiveSortOrder({
            field: urlSortField,
            order: urlSortOrder as SortOrder
          })
        }
      })
      .catch((error) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.EXCEPTIONS.GET_AN_EXCEPTION.ERROR
        })
      })
      .finally(() => {
        setLoading(false)
        store.uiStore.setGlobalLoader(false)
      })
  }

  const sortData = (sortField: string, sortOrder: SortOrder) => {
    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)
    store.uiStore.setGlobalLoader(true)

    const timeZone = store.userStore.getOrganizationPreference()?.timeZone

    const { startOfMonth, endOfCurrentDayTimestamp } = getOrgTimeRange(timeZone)
    const params: any = {
      ...allQueryParams,
      'created-from': allQueryParams['created-from'] || String(startOfMonth),
      'created-to':
        allQueryParams['created-to'] || String(endOfCurrentDayTimestamp),
      'time-zone': timeZone,
      size: StringConstants.DEFAULT_TABLE_SIZE
    }
    Object.keys(params).forEach((key) => {
      if (key.startsWith('sort-')) {
        delete params[key]
      }
    })

    // Handle "CLEAR" by removing sort-related params
    if (sortOrder !== 'CLEAR') {
      params[sortField] = sortOrder
      // Persist sort information in URL
      const searchParams = new URLSearchParams(location.search)
      searchParams.set('sortField', sortField)
      searchParams.set('sortOrder', sortOrder)
      window.history.replaceState({}, '', `?${searchParams.toString()}`)
    } else {
      delete params[sortField]
      // Remove sort params from URL
      const searchParams = new URLSearchParams(location.search)
      searchParams.delete('sortField')
      searchParams.delete('sortOrder')
      window.history.replaceState({}, '', `?${searchParams.toString()}`)
    }
    setSearchParams(params)
    viewAllGroupedExceptions(params).then((response: any) => {
      setTableData(response.data)
      setCurrentPage(response.page)
      store.exceptionsStoreV2.setGroupedExceptions(response.data)
      store.exceptionsStoreV2.setTotalCount(response.totalCount)
      setTotalCount(response.totalCount)
      store.filterStore.setFilters(response.filters)
      setLoading(false)
      store.uiStore.setGlobalLoader(false)
      store.exceptionStore.setIsRefreshed(false)

      const filters = getCommonFilters(
        response,
        startOfMonth,
        endOfCurrentDayTimestamp,
        timeZone
      )

      store.filterStore.setFilters(response.filters)
      setDateFilter(filters)
    })
  }

  const handleSortCycle = (sortField: string) => {
    // Determine the next order synchronously
    let nextOrder: SortOrder = 'ASC'
    if (activeSortOrder.field === sortField) {
      nextOrder =
        activeSortOrder.order === 'ASC'
          ? 'DESC'
          : activeSortOrder.order === 'DESC'
            ? 'CLEAR'
            : 'ASC'
    }

    // Update the sort state and call sortData immediately
    setActiveSortOrder({
      field: nextOrder !== 'CLEAR' ? sortField : null,
      order: nextOrder
    })

    sortData(sortField, nextOrder)
  }

  const fetchExceptionFilters = () => {
    setFilterLoading(true)
    getExceptionGroupFilters()
      .then((response: any) => {
        const statusFilters = response.data[2]
        statusFilters.values = statusFilters.values.filter(
          (value: any) =>
            value.displayText !== ExceptionStatusTypes.SNOOZED &&
            value.displayText !== ExceptionStatusTypes.IGNORED
        )
        response.data[2] = statusFilters

        setFilterOptions(response.data)
      })
      .catch(() => {
        ToastNotification({
          type: 'error',
          message: isFullStackProject
            ? ToastMessageConstants.EXCEPTIONS.EXCEPTION_FILTERS.ERROR
            : ToastMessageConstants.ERROR.ERROR_FILTERS.ERROR
        })
      })
      .finally(() => {
        setFilterLoading(false)
      })
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const urlSortField = searchParams.get('sortField')
    const urlSortOrder = searchParams.get('sortOrder')

    if (urlSortField && urlSortOrder) {
      // Initialize the sort state from URL
      setActiveSortOrder({
        field: urlSortField,
        order: urlSortOrder as SortOrder
      })
    }
  }, [location.search])

  useEffect(() => {
    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)
    const orgTimeZone = store.userStore.getOrganizationPreference()?.timeZone

    const { startOfMonth, endOfCurrentDayTimestamp } =
      getOrgTimeRange(orgTimeZone)
    const page = currentPage || 1

    const params: any = {
      page,
      ...allQueryParams,
      'created-from': allQueryParams['created-from'] || startOfMonth,
      'created-to': allQueryParams['created-to'] || endOfCurrentDayTimestamp,
      'time-zone': allQueryParams['time-zone'] || orgTimeZone
    }

    const initializeFiltersFromUrl = () => {
      const searchString = allQueryParams['search-string'] || ''
      if (searchString) {
        setSearchTerm(searchString)
      }
    }

    initializeFiltersFromUrl()
    const pageFromQueryParams = searchParams.get('page')

    sessionStorage.removeItem('is-bookmarked')
    sessionStorage.removeItem('severity')
    sessionStorage.removeItem('exception-status')
    sessionStorage.removeItem('assigned-to')
    sessionStorage.removeItem('exceptionGroupTab')

    if (!isEqual(params, allQueryParams)) {
      setSearchParams({ ...params })
      setCurrentPage(1)
    } else {
      fetchGroupedExceptions({
        ...params,
        size: StringConstants.DEFAULT_TABLE_SIZE
      })
      setCurrentPage(Number(pageFromQueryParams))
    }

    if (!allQueryParams['search-string']) {
      setSearchTerm('')
    }
  }, [searchParams, store.scopeStore.getScope()])

  useEffect(() => {
    fetchExceptionFilters()
  }, [])

  useEffect(() => {
    if (store.filterStore.getFilterDisabled()) {
      store.filterStore.setFilterDisabled(false)
    }
  }, [store.filterStore.getFilterDisabled()])

  useEffect(() => {
    if (store.exceptionStore.getIsRefreshed() === true) {
      const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)
      if (!allQueryParams['search-string']) {
        setSearchTerm('')
      }

      setLoading(true)
      fetchGroupedExceptions({
        ...allQueryParams,
        size: StringConstants.DEFAULT_TABLE_SIZE
      })
    }
  }, [store.exceptionStore.getIsRefreshed()])

  return loading || store.uiStore.getGlobalLoader() || !tableData ? (
    <Spinner />
  ) : (
    <div className='mt-[24px] mb-[24px] ' ref={tableRef}>
      <div className='relative w-full pb-3'>
        <Search
          id='search'
          name='Search'
          placeholder='Search'
          value={searchTerm}
          onChange={handleSearchChange}
          autoComplete='off'
          width='32vw'
          disabled={loading}
          onPressEnter={() => handleSearchEnter(searchTerm)}
          onBlur={() => {
            setTimeout(() => setShowDropdown(false), 200)
          }}
        />
        {showDropdown && (
          <div
            className='absolute z-[99999] mt-[12px] flex flex-col bg-white border items-start p-[12px] rounded-md w-[32vw] shadow-lg'
            style={{ borderColor: pallete.colors.stroke2 }}
          >
            <div
              className={`flex p-[8px] gap-[8px] items-center cursor-pointer `}
              onClick={() => handleOptionSelect(searchTerm, 'type')}
            >
              <DDSTooltip
                id={generateRandomId()}
                label={searchTerm}
                className={`border override-tooltip-arrow max-w-[90%] break-all ${
                  typeof searchTerm === 'string' && searchTerm.length > 38
                    ? searchTerm
                    : '!hidden'
                }`}
                theme='light'
                style={{
                  borderColor: pallete.colors.stroke2,
                  zIndex: 99999
                }}
              >
                <div className='flex flex-row  gap-[8px]'>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.textDark4}
                    className='flex-start truncate  w-[290px] cursor-default'
                  >
                    {searchTerm}
                  </DDSTypography.Paragraph>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.textDark6}
                  >
                    in type column
                  </DDSTypography.Paragraph>
                </div>
              </DDSTooltip>
            </div>

            <div
              className='flex p-[8px] gap-[8px] items-center cursor-pointer'
              onClick={() => handleOptionSelect(searchTerm, 'any')}
            >
              <DDSTooltip
                id={generateRandomId()}
                label={searchTerm}
                className={`border override-tooltip-arrow max-w-[90%] break-all ${
                  typeof searchTerm === 'string' && searchTerm.length > 38
                    ? searchTerm
                    : '!hidden'
                }`}
                theme='light'
                style={{
                  borderColor: pallete.colors.stroke2,
                  zIndex: 99999
                }}
              >
                <div className='flex flex-row  gap-[8px]'>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.textDark4}
                    className='flex-start truncate  w-[290px] cursor-default'
                  >
                    {searchTerm}
                  </DDSTypography.Paragraph>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.textDark6}
                  >
                    in all column
                  </DDSTypography.Paragraph>
                </div>
              </DDSTooltip>
            </div>
          </div>
        )}
      </div>
      <div className='flex items-center custom-filter justify-end absolute right-[0px]  top-[66px] space-x-[12px]'>
        <Filter
          options={filterOptions}
          dropdownLoader={filterLoading}
          primaryText='Add Filters'
          APITimeFormat={true}
          hideFilterChipAPI={true}
          clearFilterDisabled={true}
          isScrollableAPI={true}
        />
        {!(
          loading ||
          store.uiStore.getGlobalLoader() ||
          store.uiStore.getprojectEnvironmentLoader()
        ) ? (
          <SideChip
            id='date-range-chip'
            label='Date Range'
            value={convertToFormat(dateFilter, organizationTimeZone)}
          />
        ) : null}
      </div>

      {loading || tableData.length > 0 ? (
        <Table
          data={tableData}
          columns={GroupedExceptionColumns}
          totalCount={totalCount}
          onSelect={(row: any) => {
            setSelectedRows(row)
          }}
          rowsPerPage={10}
          currentPage={currentPage}
          onPageChange={(page) => {
            const allQueryParams = getAllQueryParamsAsObjectFromUrl(
              location.search
            )
            const orgTimeZone =
              store.userStore.getOrganizationPreference()?.timeZone

            const { startOfMonth, endOfCurrentDayTimestamp } =
              getOrgTimeRange(orgTimeZone)

            const updatedParams = {
              ...allQueryParams,
              page: page.toString(),
              'created-from': allQueryParams['created-from'] || startOfMonth,
              'created-to':
                allQueryParams['created-to'] || endOfCurrentDayTimestamp,
              'time-zone': allQueryParams['time-zone'] || timeZone
            }

            setLoading(true)
            setCurrentPage(page)
            setSearchParams(updatedParams)
          }}
          selectable
          tableStyle={{
            overflowX: 'scroll',

            borderColor: pallete.colors.stroke2
          }}
          loading={loading}
          selectionType='checkbox'
          className='override-checkbox-style'
          clickable={routeException}
          clickType='GroupException'
        />
      ) : searchRecordLength === 0 && searchTerm != '' ? (
        <div className='justify-center'>
          <NoRecord
            style={{ height: '70vh' }}
            imageSrc={NoFilterImage}
            text='No results found'
          />
        </div>
      ) : (
        <NoRecord
          imageSrc={NoRecordImage}
          style={{ height: '74vh' }}
          text={
            <>
              Configure {isFullStackProject ? 'Exception' : 'Error'}
              {''} Monitoring.
              <Link
                href={
                  isFullStackProject
                    ? StringConstants.FULLSTACK_URL
                    : isSalesforceProject
                      ? StringConstants.SALESFORCE_URL
                      : StringConstants.MULESOFT
                }
                target='_blank'
                rel='noopener noreferrer'
                style={{ marginLeft: '4px' }}
              >
                Learn more
              </Link>{' '}
            </>
          }
        />
      )}
      <ConfirmationPopup
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        title='Severity Type'
        primaryButtonClick={() => updatePreferences()}
        secondaryButtonClick={() => {
          setModalOpen(false)
          setApplyToExisting(null)
        }}
        loading={loading}
        primaryButtonText='Confirm'
        secondaryButtonText='Cancel'
        primaryButtonDisabled={loading || applyToExisting === null}
        secondaryButtonDisabled={loading}
        onIconClose={handleModalClose}
        content={
          <div className='flex flex-col w-full gap-y-[24px] items-center justify-center '>
            <DDSTypography.Title
              variant='medium'
              type='h5'
              className='text-left'
              color={pallete.colors.textDark11}
            >
              {`Would you like this new severity to apply only to new incoming ${isFullStackProject ? 'exceptions' : 'errors'}, or also to existing ${isFullStackProject ? 'exceptions' : 'errors'} in this group?`}
            </DDSTypography.Title>

            <DDSTypography.Title
              variant='medium'
              type='h5'
              className='text-left'
              color={pallete.colors.textDark11}
            >
              Updating existing {isFullStackProject ? 'exceptions' : 'errors'}{' '}
              will override their current severity values.
            </DDSTypography.Title>

            <DDSTypography.Title
              variant='semiBold'
              type='h5'
              className='text-left'
            >
              {`Choose how you'd like to apply this change:`}
            </DDSTypography.Title>
            <div className='flex flex-col items-start w-full'>
              <DDSRadio
                checked={applyToExisting === false}
                onChange={() => setApplyToExisting(false)}
                label={
                  <DDSTypography.Title
                    type='h5'
                    variant='medium'
                    className='pl-[8px]'
                    color={pallete.colors.textDark3}
                  >
                    {`Apply to new ${isFullStackProject ? 'exceptions' : 'errors'} only`}
                  </DDSTypography.Title>
                }
              ></DDSRadio>
              <DDSTypography.Paragraph
                size='para'
                variant='regular'
                className='pl-[24px]'
                color={pallete.colors.textDark4}
              >
                {`Keep current severity for existing ${isFullStackProject ? 'exceptions' : 'errors'}`}
              </DDSTypography.Paragraph>
            </div>
            <div className='flex flex-col items-start w-full'>
              <DDSRadio
                checked={applyToExisting === true}
                onChange={() => setApplyToExisting(true)}
                label={
                  <DDSTypography.Title
                    type='h5'
                    variant='medium'
                    className='pl-[8px]'
                    color={pallete.colors.textDark3}
                  >
                    {`Apply to all ${isFullStackProject ? 'exceptions' : 'errors'}`}
                  </DDSTypography.Title>
                }
              ></DDSRadio>
              <DDSTypography.Paragraph
                size='para'
                variant='regular'
                className='pl-[24px]'
                color={pallete.colors.textDark4}
              >
                {`Update severity for both existing and new ${isFullStackProject ? 'exceptions' : 'errors'} in this group`}
              </DDSTypography.Paragraph>
            </div>
          </div>
        }
      />
    </div>
  )
}

export default observer(GroupedExceptions)
